import { composeRefs } from "./use-composed-refs.js";
import { mergeProps } from "./merge-props.js";
import { getStateDataAttrs } from "@videojs/core/dom";
import { cloneElement, createElement, isValidElement } from "react";
import { isFunction } from "@videojs/utils/predicate";
import { resolveClassName } from "@videojs/utils/style";
//#region src/utils/use-render.tsx
function resolveStyle(style, state) {
	return isFunction(style) ? style(state) : style;
}
function getElementRef(element) {
	const elementAny = element;
	return elementAny.ref ?? elementAny.props?.ref;
}
function mergeRefs(...refs) {
	const flatRefs = refs.flat().filter((ref) => ref !== null && ref !== void 0);
	if (flatRefs.length === 0) return void 0;
	if (flatRefs.length === 1) return flatRefs[0];
	return composeRefs(...flatRefs);
}
/**
* Render a UI component element.
*
* Handles: - Default tag rendering - Render prop (element or function) - Props merging (event handlers chained,
* className concatenated, style merged) - Ref composition - className/style as functions of state
*
* @example
*   ```tsx
*   return renderElement('button', componentProps, {
*     state,
*     ref: [forwardedRef, buttonRef],
*     props: [{ type: 'button' }, elementProps, getButtonProps],
*   });
*   ```;
*
* @public
*/
function renderElement(element, componentProps, params) {
	const { className: classNameProp, style: styleProp, render } = componentProps;
	const { state, ref, props, stateAttrMap } = params;
	const className = resolveClassName(classNameProp, state);
	const style = resolveStyle(styleProp, state);
	const stateDataAttrs = stateAttrMap ? getStateDataAttrs(state, stateAttrMap) : {};
	const mergedProps = mergeProps(stateDataAttrs, ...Array.isArray(props) ? props : props ? [props] : []);
	if (className !== void 0) mergedProps.className = mergedProps.className ? `${mergedProps.className} ${className}` : className;
	if (style !== void 0) mergedProps.style = mergedProps.style ? {
		...mergedProps.style,
		...style
	} : style;
	if (isFunction(render)) {
		const mergedRef = mergeRefs(ref, mergedProps.ref);
		return render({
			...mergedProps,
			ref: mergedRef
		}, state);
	}
	if (isValidElement(render)) {
		const elementRef = getElementRef(render);
		const mergedRef = mergeRefs(ref, mergedProps.ref, elementRef);
		const elementProps = mergeProps(mergedProps, render.props);
		elementProps.ref = mergedRef;
		return cloneElement(render, elementProps);
	}
	mergedProps.ref = mergeRefs(ref, mergedProps.ref);
	return createElement(element, mergedProps);
}
//#endregion
export { renderElement };

//# sourceMappingURL=use-render.js.map